import { useEffect, useRef } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createWorkOrderAmendment,
  deleteWorkOrderAmendment,
  fetchWorkOrderAmendmentDetail,
  submitWorkOrderAmendment,
  updateWorkOrderAmendment,
} from '#/api/momMgmt/workOrder/amendment';
import { fetchWorkOrderDetail } from '#/api/momMgmt/workOrder/fetchDetail';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import WorkOrderAmendmentStatusBadge from '#/modules/momMgmt/workOrder/components/WorkOrderAmendmentStatusBadge';
import WorkOrderAmendmentSubstitutionsEditor from '#/modules/momMgmt/workOrder/components/WorkOrderAmendmentSubstitutionsEditor';
import WorkOrderExpensesEditor from '#/modules/momMgmt/workOrder/components/WorkOrderExpensesEditor';
import {
  getWorkOrderApiErrorMessage,
  isWorkOrderVersionConflict,
} from '#/modules/momMgmt/workOrder/helper/apiError';
import {
  hasWorkOrderAmendmentFormChanges,
  mapAmendmentFormToWriteValues,
  mapWorkOrderToAmendmentFormValues,
} from '#/modules/momMgmt/workOrder/helper/amendmentFormSerialize';
import { computeExpectedReceiptQty } from '#/modules/momMgmt/workOrder/helper/amendmentQty';
import {
  WORK_ORDER_AMENDMENT_STATUS,
  canDeleteWorkOrderAmendmentDraft,
  canEditWorkOrderAmendmentDraft,
  canSubmitWorkOrderAmendmentDraft,
  findOpenWorkOrderAmendment,
  hasWorkOrderSourceOutbound,
  pickAmendmentUpdatedAt,
} from '#/modules/momMgmt/workOrder/helper/amendmentStatus';
import { WORK_ORDER_STATUS } from '#/modules/momMgmt/workOrder/helper/workOrderStatus';
import {
  WORK_ORDER_AMENDMENT_FIELD_LABELS,
  resolveWorkOrderAmendmentFieldLabel,
  workOrderAmendmentFormSchema,
} from '#/modules/momMgmt/workOrder/schemas/workOrderAmendmentSchema';
import { toast } from '#/utils/toast';
import {
  workOrderAmendmentDetailQueryKey,
  workOrderAmendmentListQueryKeyPrefix,
  workOrderDetailQueryKey,
  workOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';

const DEFAULT_VALUES = {
  scrapQty: 0,
  cancelQty: 0,
  manufacturingQty: 0,
  completedQty: 0,
  issueReason: '',
  remark: '',
  mtrSubstitutions: [],
  expenses: [],
  expensesLocked: false,
};

function pickWorkOrderUpdatedAt(detail) {
  return detail?.record?.updatedAt ?? detail?.updatedAt ?? null;
}

const CreateEditWorkOrderAmendmentPage = () => {
  const { id: workOrderId, amendmentId } = useParams();
  const isEditMode = Boolean(amendmentId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const workOrderUpdatedAtRef = useRef(null);
  const canCreate = useCan(PERM.WORK_ORDER_AMENDMENT_CREATE);
  const canUpdate = useCan(PERM.WORK_ORDER_AMENDMENT_UPDATE);
  const amendmentUpdatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
  } = useFormValidationSummary();

  const workOrderQuery = useQuery({
    queryKey: workOrderDetailQueryKey(workOrderId),
    queryFn: () => fetchWorkOrderDetail(workOrderId),
    enabled: Boolean(workOrderId),
  });

  const amendmentQuery = useQuery({
    queryKey: workOrderAmendmentDetailQueryKey(workOrderId, amendmentId),
    queryFn: () => fetchWorkOrderAmendmentDetail(workOrderId, amendmentId),
    enabled: Boolean(workOrderId && amendmentId),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(workOrderAmendmentFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const scrapQty = useWatch({ control, name: 'scrapQty' });
  const cancelQty = useWatch({ control, name: 'cancelQty' });
  const manufacturingQty = useWatch({ control, name: 'manufacturingQty' });
  const completedQty = useWatch({ control, name: 'completedQty' });
  const expensesLocked = useWatch({ control, name: 'expensesLocked' });

  const workOrder = workOrderQuery.data?.data;
  const amendment = amendmentQuery.data?.data;

  useEffect(() => {
    if (!workOrder) {
      return;
    }
    if (isEditMode && !amendment) {
      return;
    }

    workOrderUpdatedAtRef.current = pickWorkOrderUpdatedAt(workOrder);
    amendmentUpdatedAtRef.current = pickAmendmentUpdatedAt(amendment);
    resetValidationSummary();
    reset(mapWorkOrderToAmendmentFormValues(workOrder, isEditMode ? amendment : null));
  }, [workOrder, amendment, isEditMode, reset, resetValidationSummary]);

  const commandMutation = useMutation({
    mutationFn: async ({ action, values }) => {
      if (action === 'create') {
        return createWorkOrderAmendment(
          workOrderId,
          mapAmendmentFormToWriteValues(values, workOrderUpdatedAtRef.current, workOrder),
        );
      }
      if (action === 'update') {
        return updateWorkOrderAmendment(
          workOrderId,
          amendmentId,
          mapAmendmentFormToWriteValues(values, amendmentUpdatedAtRef.current, workOrder),
        );
      }
      if (action === 'submit') {
        const saved = isEditMode
          ? await updateWorkOrderAmendment(
            workOrderId,
            amendmentId,
            mapAmendmentFormToWriteValues(values, amendmentUpdatedAtRef.current, workOrder),
          )
          : await createWorkOrderAmendment(
            workOrderId,
            mapAmendmentFormToWriteValues(values, workOrderUpdatedAtRef.current, workOrder),
          );
        const savedId = saved?.data?.id ?? amendmentId;
        const savedUpdatedAt = pickAmendmentUpdatedAt(saved?.data);
        return submitWorkOrderAmendment(workOrderId, savedId, {
          updatedAt: savedUpdatedAt,
        });
      }
      if (action === 'delete') {
        return deleteWorkOrderAmendment(workOrderId, amendmentId);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      await queryClient.invalidateQueries({ queryKey: workOrderListQueryKeyPrefix() });
      await queryClient.invalidateQueries({ queryKey: workOrderDetailQueryKey(workOrderId) });
      await queryClient.invalidateQueries({
        queryKey: workOrderAmendmentListQueryKeyPrefix(workOrderId),
      });

      if (variables.action === 'create') {
        const createdId = result?.data?.id;
        if (createdId) {
          navigate(ROUTES.workOrderAmendmentEdit(workOrderId, createdId));
        }
        return;
      }

      if (variables.action === 'submit' || variables.action === 'delete') {
        navigate(ROUTES.workOrderDetail(workOrderId));
        return;
      }

      amendmentUpdatedAtRef.current = pickAmendmentUpdatedAt(result?.data);
      if (amendmentId) {
        await queryClient.invalidateQueries({
          queryKey: workOrderAmendmentDetailQueryKey(workOrderId, amendmentId),
        });
      }
    },
    onError: (error) => {
      toast.error(getWorkOrderApiErrorMessage(error));
      if (isWorkOrderVersionConflict(error)) {
        workOrderQuery.refetch();
        if (isEditMode) {
          amendmentQuery.refetch();
        }
      }
    },
  });

  const isPending = commandMutation.isPending;
  const expectedReceiptQty = computeExpectedReceiptQty(
    manufacturingQty,
    scrapQty,
    cancelQty,
  );
  const substitutionsLocked = hasWorkOrderSourceOutbound(workOrder);
  const openAmendment = findOpenWorkOrderAmendment(workOrder?.amendments);
  const isPageLoading = workOrderQuery.isLoading
    || (isEditMode && amendmentQuery.isLoading);
  const isPageError = workOrderQuery.isError
    || (isEditMode && amendmentQuery.isError);

  if (!isPageLoading && workOrder && workOrder.status !== WORK_ORDER_STATUS.PROCESSING) {
    return <Navigate to={ROUTES.workOrderDetail(workOrderId)} replace />;
  }

  if (!isPageLoading && workOrder && !isEditMode && openAmendment) {
    if (openAmendment.status === WORK_ORDER_AMENDMENT_STATUS.DRAFT && openAmendment.id) {
      return (
        <Navigate
          to={ROUTES.workOrderAmendmentEdit(workOrderId, openAmendment.id)}
          replace
        />
      );
    }
    return <Navigate to={ROUTES.workOrderDetail(workOrderId)} replace />;
  }

  if (!isPageLoading && isEditMode && amendment
    && !canEditWorkOrderAmendmentDraft(amendment, workOrder?.status)) {
    return <Navigate to={ROUTES.workOrderDetail(workOrderId)} replace />;
  }

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSave(values) {
    markSubmitAttempted();
    if (isPending) {
      return;
    }
    if (!hasWorkOrderAmendmentFormChanges(values, workOrder)) {
      toast.error('請至少變更報廢量、取消量、一筆替代料或費用');
      return;
    }
    commandMutation.mutate({
      action: isEditMode ? 'update' : 'create',
      values,
    });
  }

  function onSubmitDraft(values) {
    markSubmitAttempted();
    if (isPending) {
      return;
    }
    if (!hasWorkOrderAmendmentFormChanges(values, workOrder)) {
      toast.error('請至少變更報廢量、取消量、一筆替代料或費用');
      return;
    }
    commandMutation.mutate({ action: 'submit', values });
  }

  async function handleDelete() {
    const ok = await confirm({
      title: '刪除異動單草稿',
      message: '刪除後無法復原，確定刪除？',
      confirmText: '刪除',
      destructive: true,
    });
    if (!ok || isPending) {
      return;
    }
    commandMutation.mutate({ action: 'delete' });
  }

  const pageTitle = isEditMode ? '編輯異動單' : '新建異動單';
  const canSave = isEditMode ? canUpdate : canCreate;
  const canSubmit = canCreate && (
    !isEditMode || canSubmitWorkOrderAmendmentDraft(amendment, workOrder?.status)
  );
  const canDelete = canCreate
    && isEditMode
    && canDeleteWorkOrderAmendmentDraft(amendment, workOrder?.status);

  return (
    <PageShell variant="formTall">
      {ConfirmDialogHost}
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              {isEditMode && amendment?.status ? (
                <WorkOrderAmendmentStatusBadge status={amendment.status} size="sm" />
              ) : null}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.workOrderDetail(workOrderId)}>返回詳情</Link>
              </Button>
            </div>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : null}

          {isPageError ? (
            <p className="text-sm text-destructive">
              {getWorkOrderApiErrorMessage(
                workOrderQuery.error || amendmentQuery.error,
                '找不到異動單資料',
              )}
            </p>
          ) : null}

          {!isPageLoading && !isPageError && workOrder ? (
            <>
              <p className="mb-3 text-sm text-slate-600">
                {`工單 ${workOrder.workOrderNo || '—'} ／ 製造量 ${manufacturingQty ?? '—'}`}
                {` ／ 已入庫 ${completedQty ?? '—'} ／ 應入庫 ${expectedReceiptQty ?? '—'}`}
              </p>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={WORK_ORDER_AMENDMENT_FIELD_LABELS}
                fieldLabelResolver={resolveWorkOrderAmendmentFieldLabel}
              />
              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSave, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">異動內容</h2>
                    </div>
                    <div
                      className={cn(
                        'grid grid-cols-1 gap-3',
                        '@4xl/sheet:grid-cols-2',
                      )}
                    >
                      <FloatingInput
                        id="scrapQty"
                        label="報廢量"
                        type="number"
                        min={0}
                        step="any"
                        register={register}
                        errors={errors}
                        disabled={isPending}
                      />
                      <FloatingInput
                        id="cancelQty"
                        label="取消量"
                        type="number"
                        min={0}
                        step="any"
                        register={register}
                        errors={errors}
                        disabled={isPending}
                      />
                    </div>
                    <div
                      className={cn(
                        'mt-3 grid grid-cols-1 gap-3',
                        '@4xl/sheet:grid-cols-2',
                      )}
                    >
                      <FloatingTextarea
                        id="issueReason"
                        label="事由"
                        register={register}
                        errors={errors}
                        rows={2}
                        disabled={isPending}
                      />
                      <FloatingTextarea
                        id="remark"
                        label="備註"
                        register={register}
                        errors={errors}
                        rows={2}
                        disabled={isPending}
                      />
                    </div>
                    <WorkOrderAmendmentSubstitutionsEditor
                      control={control}
                      register={register}
                      setValue={setValue}
                      errors={errors}
                      locked={substitutionsLocked}
                    />
                  </section>

                  <section className="pt-2.5 pb-5">
                    <WorkOrderExpensesEditor
                      control={control}
                      register={register}
                      errors={errors}
                      disabled={isPending || expensesLocked}
                      description={expensesLocked
                        ? '此異動單建立於費用功能上線前，無法加入費用變更；如需調整費用請另建異動單。'
                        : '擬議費用核准後才套用到工單；既有費用不可移除，不再收取請把金額改為 0。'}
                    />
                  </section>
                </div>

                <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.workOrderDetail(workOrderId)}>取消</Link>
                  </Button>
                  {canDelete ? (
                    <Button
                      type="button"
                      variant="destructive"
                      className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
                      disabled={isPending}
                      onClick={handleDelete}
                    >
                      刪除草稿
                    </Button>
                  ) : null}
                  {canSave ? (
                    <Button type="submit" disabled={isPending}>
                      {isPending ? '處理中…' : '儲存'}
                    </Button>
                  ) : null}
                  {canSubmit ? (
                    <Button
                      type="button"
                      disabled={isPending}
                      onClick={handleSubmit(onSubmitDraft, handleInvalidSubmit)}
                    >
                      提交
                    </Button>
                  ) : null}
                </div>
              </form>
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditWorkOrderAmendmentPage;
